import ExerciseLibraryEntry from './components/training/ExerciseLibraryEntry';
/*
 * src/main.tsx
 *
 * This is the main entry point for the React application.
 * It is responsible for:
 * - Rendering the root React component (<App /> or <Maintenance />).
 * - Setting up the React Router <BrowserRouter /> for client-side routing.
 * - Wrapping the application with the <I18nProvider /> for internationalization.
 * - Importing global CSS styles.
 * - Checking for maintenance mode and rendering the appropriate component.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import App from './App';
import { I18nProvider, useI18n } from './i18n';
import Maintenance from './pages/Maintenance';
import Login from './pages/Login';
import RegisterPage from './pages/Register';
import GuestEvaluationAccess from './pages/GuestEvaluationAccess';
import GuestEvaluationFill from './pages/GuestEvaluationFill';
import GuestEvaluationThanks from './pages/GuestEvaluationThanks';
import PublicTeamIntake from './pages/PublicTeamIntake';
import { getMaintenanceMode } from './utils/maintenance';
import './index.css';
import RuntimeMigrationGate from './components/RuntimeMigrationGate';
import { PwaInstallPrompt, PwaUpdatePrompt } from './components/PwaLifecycle';
import { registerPortalPwa } from './pwa/registerPwa';
import { OfflineTrainingLibrary, PublicTrainingExerciseViewer, TrainingShareResolver } from './pages/PublicTrainingRoutes';
import { consumeMobileCompatibilityHandoff } from '@waedo/frontend-core/compatibility-handoff';
import { trackEvent } from './utils/analytics';
import AnalyticsRouteTracker from './components/AnalyticsRouteTracker';
import { isTrainerPortalHostname } from './utils/trainerHost';
import { isLivePortalHostname } from './utils/liveHost';
import { API_CONNECTION_FAILED, monitorApiFetch } from './lib/apiConnectionMonitor';
import { getEnv } from './utils/env';

window.fetch = monitorApiFetch(
  window.fetch.bind(window),
  getEnv('VITE_AI_API_URL', ''),
  window.location.href,
  () => window.dispatchEvent(new Event(API_CONNECTION_FAILED)),
);

const PublicLiveWatch = React.lazy(() => import('./pages/PublicLiveWatch'));
const CuratedExerciseLibrary = React.lazy(() => import('./pages/CuratedExerciseLibrary'));
const PublicTrainingLibrary = React.lazy(() => import('./pages/PublicTrainingLibrary'));
const PublicTrainerDugout = React.lazy(() => import('./pages/PublicTrainerDugout'));

const publicTrainerDugout = (
  <React.Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-600">Trainer Dugout laden…</div>}>
    <PublicTrainerDugout />
  </React.Suspense>
);
const isTrainerEntryHost = isTrainerPortalHostname(window.location.hostname);
const isLiveEntryHost = isLivePortalHostname(window.location.hostname);

// Entry point of the application. The App component is rendered inside a
// BrowserRouter so that all pages can leverage React Router.

const compatibilityHandoff = consumeMobileCompatibilityHandoff(window.location.href);
if (compatibilityHandoff) {
  window.history.replaceState(window.history.state, '', compatibilityHandoff.cleanPath);
  trackEvent('mobile_compatibility_handoff', { route_group: compatibilityHandoff.routeGroup });
}

const PublicLiveLoading = () => {
  const { t } = useI18n();
  return <div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-600">{t('public_live_loading')}</div>;
};

const root = ReactDOM.createRoot(document.getElementById('root')!);

const isMaintenance = getMaintenanceMode();
if (isMaintenance) {
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <I18nProvider>
          <AnalyticsRouteTracker />
          <Maintenance />
        </I18nProvider>
      </BrowserRouter>
    </React.StrictMode>,
  );
} else {
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <I18nProvider>
          <AnalyticsRouteTracker />
          <RuntimeMigrationGate>
            <Routes>
              {isTrainerEntryHost ? <Route path="/" element={publicTrainerDugout} /> : null}
              {isLiveEntryHost ? <Route path="/" element={<Navigate to="/live" replace />} /> : null}
              <Route path="/login" element={<Login />} />
              <Route path="/:locale/login" element={<Login />} />
              <Route path="/auth/verify" element={<Login />} />
              <Route path="/:locale/auth/verify" element={<Login />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/:locale/register" element={<RegisterPage />} />
              <Route path="/intake" element={<PublicTeamIntake />} />
              <Route path="/intake/:publicToken" element={<PublicTeamIntake />} />
              <Route path="/evaluation-access" element={<GuestEvaluationAccess />} />
              <Route path="/evaluation-fill" element={<GuestEvaluationFill />} />
              <Route path="/evaluation-thanks" element={<GuestEvaluationThanks />} />
              {isLiveEntryHost ? <Route path="/live" element={<React.Suspense fallback={<PublicLiveLoading />}><PublicLiveWatch /></React.Suspense>} /> : null}
              <Route path="/live/watch" element={isLiveEntryHost ? <Navigate to={`/live${window.location.search}`} replace /> : <React.Suspense fallback={<PublicLiveLoading />}><PublicLiveWatch /></React.Suspense>} />
              <Route path="/exercise-library" element={<React.Suspense fallback={<div role="status">…</div>}><ExerciseLibraryEntry fallback={<CuratedExerciseLibrary />} /></React.Suspense>} />
              <Route path="/:locale/exercise-library" element={<React.Suspense fallback={<div role="status">…</div>}><ExerciseLibraryEntry fallback={<CuratedExerciseLibrary />} /></React.Suspense>} />
              <Route path="/training-library" element={<React.Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-600">Trainingsbibliotheek laden…</div>}><PublicTrainingLibrary /></React.Suspense>} />
              <Route path="/:locale/training-library" element={<React.Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-600">Trainingsbibliotheek laden…</div>}><PublicTrainingLibrary /></React.Suspense>} />
              <Route path="/training-library/:kind" element={<React.Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-600">Trainingsbibliotheek laden…</div>}><PublicTrainingLibrary /></React.Suspense>} />
              <Route path="/:locale/training-library/:kind" element={<React.Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-600">Trainingsbibliotheek laden…</div>}><PublicTrainingLibrary /></React.Suspense>} />
              <Route path="/training-library/:kind/:id" element={<React.Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-600">Trainingscontent laden…</div>}><PublicTrainingLibrary /></React.Suspense>} />
              <Route path="/:locale/training-library/:kind/:id" element={<React.Suspense fallback={<div className="flex min-h-screen items-center justify-center bg-gray-50 text-sm text-gray-600">Trainingscontent laden…</div>}><PublicTrainingLibrary /></React.Suspense>} />
              <Route path="/trainer" element={publicTrainerDugout} />
              <Route path="/:locale/trainer" element={publicTrainerDugout} />
              <Route path="/share/:key" element={<TrainingShareResolver />} />
              <Route path="/:locale/share/:key" element={<TrainingShareResolver />} />
              <Route path="/trainer/share/:key" element={<TrainingShareResolver />} />
              <Route path="/:locale/trainer/share/:key" element={<TrainingShareResolver />} />
              <Route path="/trainer/viewer" element={<PublicTrainingExerciseViewer />} />
              <Route path="/:locale/trainer/viewer" element={<PublicTrainingExerciseViewer />} />
              <Route path="/trainer/offline" element={<OfflineTrainingLibrary />} />
              <Route path="/:locale/trainer/offline" element={<OfflineTrainingLibrary />} />
              <Route path="/offline" element={<OfflineTrainingLibrary />} />
              <Route path="/*" element={<App />} />
            </Routes>
          </RuntimeMigrationGate>
          <PwaInstallPrompt />
          <PwaUpdatePrompt />
        </I18nProvider>
      </BrowserRouter>
    </React.StrictMode>,
  );
}

void registerPortalPwa();
